import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import Marquee from 'react-fast-marquee';
import SmoothWaveText from '../../Components/SmoothWaveText';
import { useNavigate } from 'react-router-dom';

type VideoItem = {
    label: string;
    caption: string;
    iframe: string;
};

// Helper to extract YouTube video ID from iframe URL
const getYoutubeId = (url: string) => {
    const match = url.match(/embed\/([\w-]+)/);
    return match ? match[1] : '';
};

const getYoutubeThumbnail = (url: string) => {
    const id = getYoutubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
};

const videoList: VideoItem[] = [
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/7cWmf_thsrc',

    },
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/ifFX7VviGKM',

    },
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/cuUIFfK5CAw',
    },
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/M6TNfdCYHvs',
    },
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/jYdbty9Un4Q',
    },
    {
        label: '',
        caption: '',
        iframe: 'https://www.youtube.com/embed/nb_1F_oWIqU',
    },
];

// const marqueeWords = [
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
//   'updates',
// ];

const VideoPlaylist: React.FC = () => {
    const [currentVideo, setCurrentVideo] = useState<VideoItem>(videoList[0]);
    const [isVideoVisible, setIsVideoVisible] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLDivElement>(null);

    const handleWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (scrollRef.current) {
            scrollRef.current.scrollLeft += event.deltaY;
        }
    };

    // Intersection Observer to detect when video section is visible
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVideoVisible(true);
                    } else {
                        setIsVideoVisible(false);
                    }
                });
            },
            {
                threshold: 0.5, // Trigger when 50% of the video section is visible
                rootMargin: '0px 0px -10% 0px', // Add some margin for better UX
            },
        );

        const currentVideoRef = videoRef.current;
        if (currentVideoRef) {
            observer.observe(currentVideoRef);
        }

        return () => {
            if (currentVideoRef) {
                observer.unobserve(currentVideoRef);
            }
        };
    }, []);

    // Function to get video URL with proper autoplay control
    const getVideoUrl = (video: VideoItem) => {
        const baseUrl = video.iframe.split('?')[0];
        return isVideoVisible ? `${baseUrl}?autoplay=1&mute=1` : baseUrl;
    };

    return (
        <Box sx={{ py: { xs: 6, md: 4 }, bgcolor: 'transparent', }}>
            <Typography
                variant="h3"
                textAlign="center"
                sx={{

                    letterSpacing: "4px",
                    mb: 5,
                    color: 'white'
                }}
            >
               SEE OUR APPROACH IN ACTION
            </Typography>

            <Box sx={{ maxWidth: { xs: 'auto', md: '1000px' }, mx: 'auto', pt: 5 }}>
                <Box
                    ref={videoRef}
                    id="video-player"
                    sx={
                        {
                            // border: '5px solid rgba(32, 58, 144, 0.75)',
                            // backgroundColor: 'rgba(111, 137, 203, 0.75)',
                        }
                    }
                >
                    <Box
                        component="iframe"
                        src={getVideoUrl(currentVideo)}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                            aspectRatio: '16 / 9',
                            width: '100%',
                            height: 'auto',
                            backgroundColor: 'black',
                            border: 0,
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: 'Montserrat',
                            color: 'white',
                            fontSize: { xs: 18, sm: 20 },
                            padding: '10px 15px',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            textShadow:
                                '2px 0 rgba(0,0,0,0.5), 0 2px rgba(0,0,0,0.5), -2px 0 rgba(0,0,0,0.5), 0 -2px rgba(0,0,0,0.5)',
                        }}
                    >
                        {currentVideo.caption}
                    </Typography>

                    <Box
                        ref={scrollRef}
                        onWheel={handleWheelScroll}
                        sx={{
                            // backgroundColor: 'rgba(32, 58, 144, 0.75)',
                            display: 'grid',
                            gap: '10px',
                            gridAutoFlow: 'column',
                            gridAutoColumns: {
                                xs: 'minmax(163px, 1fr)',
                                sm: 'minmax(190px, 1fr)',
                            },
                            overflowX: 'auto',
                            py: '15px',
                            px: '5px',
                        }}
                    >
                        {videoList.map((video, index) => (
                            <Button
                                key={index}
                                onClick={() => setCurrentVideo(video)}
                                sx={{
                                    aspectRatio: '16 / 9',
                                    width: { xs: '163px', md: '190px' },
                                    height: '150px',
                                    color: 'white',
                                    fontFamily: 'Montserrat',
                                    fontSize: { xs: 15, sm: 18 },
                                    textTransform: 'lowercase',
                                    backgroundImage: `url(${getYoutubeThumbnail(video.iframe)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    border: `2px solid ${video.iframe === currentVideo.iframe
                                            ? 'rgba(255,255,255,1)'
                                            : 'rgba(20, 101, 139, 1)'
                                        }`,
                                    filter:
                                        video.iframe === currentVideo.iframe
                                            ? 'brightness(1)'
                                            : 'brightness(0.7)',
                                    textShadow:
                                        '2px 0 rgba(0,0,0,0.5), 0 2px rgba(0,0,0,0.5), -2px 0 rgba(0,0,0,0.5), 0 -2px rgba(0,0,0,0.5)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        filter: 'brightness(1)',
                                        borderColor: 'rgba(223, 197, 111, 0.90)',
                                    },
                                }}
                            >
                                {video.label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default VideoPlaylist;
